import { useLocation, useSearchParams } from 'react-router-dom';
import { useCallback, useMemo } from 'react';
import type { StatusFilter, SortOrder } from '../types/triage';

export function useTriageParams() {
  const location = useLocation();
  const [, setSearchParams] = useSearchParams();

  // Read straight from location.search, not useSearchParams. In react-router v7
  // the [searchParams] tuple can be stale after setSearchParams(..., {replace:true})
  // because the hook subscribes to a different signal than location updates;
  // useLocation always reflects the current URL synchronously across navigations.
  const params = useMemo(() => {
    const sp = new URLSearchParams(location.search);
    return {
      locationId: sp.get('loc') || null,
      ticketId: sp.get('ticket') ? Number(sp.get('ticket')) : null,
      issueId: sp.get('issue') ? Number(sp.get('issue')) : null,
      statusFilter: (sp.get('status') || 'open') as StatusFilter,
      classFilter: sp.get('class') ? Number(sp.get('class')) : null,
      priorityFilter: sp.get('priority') as 'high' | 'low' | null,
      // Priority-first by default (was oldest) — land the operator on the
      // highest-impact issue, not the oldest row.
      sortOrder: (sp.get('sort') || 'priority') as SortOrder,
    };
  }, [location.search]);

  const setParam = useCallback((key: string, value: string | null) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (value === null) {
        next.delete(key);
      } else {
        next.set(key, value);
      }
      return next;
    });
  }, [setSearchParams]);

  return { ...params, setParam, setSearchParams };
}
